/** Derived values over the loaded app data. */
import { useEffect, useMemo, useState } from 'react';
import { useAppData, useLoadedData } from './app-data.tsx';
import { useSession } from './session.tsx';
import { usePlacements } from '../data/placement.ts';
import { appDay, msUntilNextDay, type IsoDay } from '../core/date.ts';
import { dailyGoal, streak, weeklyXp, type DailyGoal } from '../core/progress.ts';
import { pathStatuses, type PathStatus } from '../core/path.ts';
import { buildReviewQueue, cardsForLang, type ReviewQueue } from '../core/review-queue.ts';
import type { Card, LessonMeta } from '../data/api.ts';
import { prefetchLesson } from '../data/lessons.ts';
import type { PathItem } from '../ui/LessonPath.tsx';

export interface Stats {
  streak: number;
  weekly: number;
  goal: DailyGoal;
}

/** Today's app day; re-renders at midnight so open screens roll over. */
export function useToday(): IsoDay {
  const [today, setToday] = useState(() => appDay(new Date()));
  useEffect(() => {
    // +1s guards against timers firing a hair early; visibility covers suspended tabs.
    const timer = setTimeout(() => setToday(appDay(new Date())), msUntilNextDay(new Date()) + 1000);
    const onVisible = () => document.visibilityState === 'visible' && setToday(appDay(new Date()));
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [today]);
  return today;
}

export function useStats(): Stats {
  const { xpEvents, profile } = useLoadedData();
  const today = useToday();
  return useMemo(() => {
    return {
      streak: streak(xpEvents, today),
      weekly: weeklyXp(xpEvents, today),
      goal: dailyGoal(xpEvents, today, profile.daily_goal_xp),
    };
  }, [xpEvents, profile.daily_goal_xp, today]);
}

export interface CoursePath {
  /** Lessons of the book that holds the current lesson. */
  items: PathItem[];
  bookTitle: string | null;
  current: LessonMeta | null;
  doneCount: number;
}

interface PathStatusIndex {
  ordered: LessonMeta[];
  done: Set<string>;
  statuses: Map<string, PathStatus>;
}

/** Lessons of the active course in path order with their done/current/locked statuses. */
function usePathStatusIndex(): PathStatusIndex {
  const { lessons, books, progress, courses, activeCourseId } = useLoadedData();
  const placements = usePlacements(useSession().session?.user.id ?? '');
  const lang = courses.find((c) => c.id === activeCourseId)?.lang;
  // A placement test opens every lesson before its recommended start.
  const startId = (lang && placements[lang]?.score.startLessonId) || null;
  return useMemo(() => {
    const bookOrder = new Map(books.map((b) => [b.id, b.sort]));
    const ordered = [...lessons].sort(
      (a, b) => (bookOrder.get(a.book_id) ?? 0) - (bookOrder.get(b.book_id) ?? 0) || a.number - b.number,
    );
    const done = new Set(Object.values(progress).filter((p) => p.status === 'done').map((p) => p.lesson_id));
    return { ordered, done, statuses: pathStatuses(ordered, done, startId) };
  }, [lessons, books, progress, startId]);
}

/** Path status of one lesson of the active course; null for a lesson outside it. */
export function useLessonPathStatus(lessonId: string): PathStatus | null {
  return usePathStatusIndex().statuses.get(lessonId) ?? null;
}

export function useCoursePath(): CoursePath {
  const { books } = useLoadedData();
  const { ordered, done, statuses } = usePathStatusIndex();
  return useMemo(() => {
    const current = ordered.find((l) => statuses.get(l.id) === 'current') ?? null;
    // Nothing current (all available lessons done): stay on the book of the last finished
    // lesson rather than jumping to the final book of not-yet-imported lessons.
    const bookId =
      current?.book_id ?? [...ordered].reverse().find((l) => done.has(l.id))?.book_id ?? books[0]?.id;
    const inBook = ordered.filter((l) => l.book_id === bookId);
    return {
      items: inBook.map((lesson) => ({ lesson, status: statuses.get(lesson.id) ?? 'locked' })),
      bookTitle: books.find((b) => b.id === bookId)?.title ?? null,
      current,
      doneCount: inBook.filter((l) => done.has(l.id)).length,
    };
  }, [ordered, done, statuses, books]);
}

/**
 * Several mounted components (tab badge, home tiles) ask for the queue at once with slightly
 * different `now`s; a minute's difference never matters here, so they share one build.
 */
let lastQueue: { cards: readonly Card[]; seen: Record<string, number>; now: number; queue: ReviewQueue<Card> } | null = null;
const QUEUE_REUSE_MS = 60_000;

function sharedReviewQueue(cards: readonly Card[], now: Date, seen: Record<string, number>): ReviewQueue<Card> {
  if (lastQueue && lastQueue.cards === cards && lastQueue.seen === seen && Math.abs(lastQueue.now - now.getTime()) < QUEUE_REUSE_MS) {
    return lastQueue.queue;
  }
  const queue = buildReviewQueue(cards, now, seen);
  lastQueue = { cards, seen, now: now.getTime(), queue };
  return queue;
}

const NO_NEW_SEEN: Record<string, number> = {};

/** Shared across components so the scoped array (and the queue built on it) keeps its identity. */
let lastScoped: { cards: readonly Card[]; lang: string | null; result: Card[] } | null = null;

/**
 * The active course's cards. Words are per language (the Words tab, review queue, due
 * counts); XP, streak and the league stay global.
 */
export function useCourseCards(): Card[] {
  const { cards } = useLoadedData();
  const lang = useAppData().activeCourse?.lang ?? null;
  if (lastScoped && lastScoped.cards === cards && lastScoped.lang === lang) return lastScoped.result;
  const result = cardsForLang(cards, lang);
  lastScoped = { cards, lang, result };
  return result;
}

export function useReviewQueue(now: Date): ReviewQueue<Card> {
  const { newSeenToday, newSeenDay } = useLoadedData();
  const cards = useCourseCards();
  const today = useToday();
  const seen = newSeenDay === today ? newSeenToday : NO_NEW_SEEN;
  return useMemo(() => sharedReviewQueue(cards, now, seen), [cards, seen, now]);
}

/**
 * Downloads the current lesson and the next imported one once the screen is idle, so
 * opening either is instant (and works offline later).
 */
export function usePrefetchLessons(path: CoursePath): void {
  const current = path.current;
  const next = useMemo(() => {
    if (!current) return null;
    const at = path.items.findIndex((i) => i.lesson.id === current.id);
    return path.items.slice(at + 1).find((i) => i.lesson.available)?.lesson ?? null;
  }, [path.items, current]);
  useEffect(() => {
    const targets = [current, next].filter((l): l is LessonMeta => l !== null && l.available);
    if (targets.length === 0) return;
    const run = () => targets.forEach((l) => prefetchLesson(l.id, l.revision));
    const idle = window.requestIdleCallback?.(run, { timeout: 2000 });
    const timer = idle === undefined ? window.setTimeout(run, 300) : undefined;
    return () => {
      if (idle !== undefined) window.cancelIdleCallback(idle);
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [current, next]);
}
